// janus-engine/minigames/rows.ts — Lane L5: the row readers every minigame resolver shares (keys, lockpicking, digging).
// Pure: no window, no fetch. A "row" is a compiled inventory / creature node as the page holds it (the encounter roster,
// a carrier's `inventory` tree, nested `inventory_in_container`).
import { statusItemId, statusRows } from '../statuses/expiry.js';

export type Row = Record<string, unknown>;
export const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
export const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
export const isUuid = (value: unknown): boolean => /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(upper(value));

export const MINIGAME_TRUTH_SCHEMA = 'janus.minigame.truth.v1';
/** StatusEffect::getId('locked') / ('lockpicking'); the Lockpicking Tools template (lockpick_sweet_spot_inc.php). */
export const LOCKED_STATUS_ID = '477A7977-60D2-4B9C-96B0-6ED4BFD6437E';
export const LOCKPICKING_STATUS_ID = 'FF9A38A3-5F35-F083-E153-7DF41B0C0826';
export const LOCKPICK_TOOLS_ITEM_ID = '12EC56F1-CE2F-0989-182F-48302A4D06B2';

export function inventoryBucket(row: unknown): Row | null { return asRow(asRow(row)?.inventory); }
export function inventoryIdOf(row: unknown): string { return upper(inventoryBucket(row)?.inventory_id ?? asRow(row)?.inventory_id); }
export function templateIdOf(row: unknown): string { return upper(inventoryBucket(row)?.inventory_item_id ?? asRow(row)?.inventory_item_id); }
export function qtyOf(row: unknown): number { const n = Number(inventoryBucket(row)?.item_qty ?? asRow(row)?.item_qty ?? 1); return Number.isFinite(n) ? n : 0; }
export function childrenOf(row: unknown): Row[] {
  const r = asRow(row);
  const kids = r?.inventory_in_container ?? r?.container_contents;
  return Array.isArray(kids) ? kids.map(asRow).filter((k): k is Row => !!k) : [];
}

export interface WalkedRow { row: Row; holders: string[] }
/** Depth-first over rows and their nested contents; `holders` = the inventory ids above the row (outermost first). */
export function* walkRows(rows: unknown, holders: string[] = [], depth = 0): Generator<WalkedRow> {
  if (!Array.isArray(rows) || depth > 8) return;
  for (const raw of rows) {
    const row = asRow(raw);
    if (!row) continue;
    yield { row, holders };
    const id = inventoryIdOf(row);
    const kids = childrenOf(row);
    if (kids.length) yield* walkRows(kids, id ? [...holders, id] : holders, depth + 1);
  }
}
export function findRow(rootLists: unknown[], inventoryId: string): WalkedRow | null {
  const want = upper(inventoryId);
  if (!want) return null;
  for (const list of rootLists) for (const hit of walkRows(list)) if (inventoryIdOf(hit.row) === want) return hit;
  return null;
}

/** A row's status entries in one direction (array or keyed-object shapes). */
export function statusEntries(row: unknown, mode: 'caster' | 'target'): Row[] {
  return statusRows(asRow(row) ?? {}, mode);
}
export function statusItemOf(entry: Row): string {
  return statusItemId(entry) || upper(entry.item_id ?? asRow(entry.items)?.item_id);
}
export function hasStatus(row: unknown, mode: 'caster' | 'target', statusItemId: string): boolean {
  const want = upper(statusItemId);
  return statusEntries(row, mode).some(e => statusItemOf(e) === want);
}
/** A lock row is LOCKED while it casts `locked` (lockpicking_lock_is_locked / use_key's unlock gate). */
export const lockIsLocked = (lockRow: unknown): boolean => hasStatus(lockRow, 'caster', LOCKED_STATUS_ID);
/** Relock requires the row locked in NEITHER direction (use_key_inc.php:236-248). */
export const lockIsLockedEitherWay = (lockRow: unknown): boolean => lockIsLocked(lockRow) || hasStatus(lockRow, 'target', LOCKED_STATUS_ID);
